import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AddOutlined, ChevronRight } from '@mui/icons-material';
import { Box, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import httpInstances from '@/api/axiosInstance';
import { dashboardQueryKeys } from '@/features/Dashboard/api/queries';
import { useEditDashboardDraft } from '@/features/Dashboard/context/EditDashboardDraft';
import { getTabContent } from '@/features/Dashboard/lib/transformers';
import { DashboardTab, MarketApps, TileBigNumber, TilesWidgetV2 } from '@/features/Dashboard/types';
import { getUniqueTabName } from '@/features/Dashboard/utils/getUniqueTabName';
import { useDashboard } from '@/hooks/api/dashboard';
import useSnackbarNotifications from '@/hooks/useSnackbarNotifications';
import { generateUniqueUUID } from '@/utils/generateUniqueUUID';

interface MoveTileToDashboardMenuProps {
  tile: TileBigNumber;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  currentDashboardName: string;
  currentTabName: string;
  onMoveToDraft: (tabName: string, tile: TileBigNumber) => void;
  onRemoveFromCurrent: () => void;
}

const menuItemStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  p: 1,
  pr: 2,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  }
};

const activeMenuItemStyles = {
  ...menuItemStyles,
  backgroundColor: 'rgba(255, 255, 255, 0.08)'
};

const MoveTileToDashboardMenu = ({
  tile,
  anchorEl,
  open,
  onClose,
  currentDashboardName,
  currentTabName,
  onMoveToDraft,
  onRemoveFromCurrent
}: MoveTileToDashboardMenuProps) => {
  const { data: allDashboards } = useDashboard();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbarNotifications();
  const queryClient = useQueryClient();
  const {
    getAllTabDrafts,
    dashboardName: draftDashboardName,
    initialDashboardName,
    draftTabNames,
    draftTabs,
    currentTabId
  } = useEditDashboardDraft();

  const [hoveredDashboard, setHoveredDashboard] = useState<string | null>(null);
  const [hoveredDashboardRef, setHoveredDashboardRef] = useState<HTMLElement | null>(null);
  const dashboardItemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const subMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Update ref for hovered dashboard in effect to avoid accessing refs during render
  useEffect(() => {
    if (!hoveredDashboard) {
      setHoveredDashboardRef(null);
      return;
    }
    const ref = dashboardItemRefs.current.get(hoveredDashboard) || null;
    setHoveredDashboardRef(ref);
  }, [hoveredDashboard]);

  // Use draft dashboard name if available, otherwise use currentDashboardName
  const effectiveCurrentDashboardName = draftDashboardName || currentDashboardName;

  // Mutation for moving tile to another dashboard
  const { mutate: moveTileToDashboard, isPending } = useMutation({
    mutationFn: async ({
      dashboardName,
      tabName,
      payload
    }: {
      dashboardName: string;
      tabName: string;
      payload: DashboardTab;
    }) => {
      const response = await httpInstances.axiosInternalInstance.post(
        `/config/dashboard/${encodeURIComponent(dashboardName)}`,
        payload
      );
      await new Promise<void>(resolve => {
        setTimeout(() => {
          resolve();
        }, 2000);
      });
      return response;
    },
    onSuccess: async () => {
      showSuccessSnackbar('Tile moved to dashboard!');
      await queryClient.refetchQueries({ queryKey: dashboardQueryKeys.all });
      onClose();
    },
    onError: () => {
      showErrorSnackbar("Couldn't move tile to dashboard. Please try again.");
    }
  });

  const convertTileToTilesWidget = useCallback(
    (tile: TileBigNumber, existingTiles: TileBigNumber[] = []): TilesWidgetV2 => {
      // Generate unique IDs
      const existingTileIds = new Set<string>();
      const existingAppIds = new Set<string>();

      // Collect existing IDs from all dashboards
      Object.values(allDashboards || {}).forEach(dashboardTabs => {
        Object.values(dashboardTabs).forEach(tab => {
          const tabContent = getTabContent(tab);
          tabContent.forEach((app: any) => {
            if (app.id) existingAppIds.add(app.id);
            if ('type' in app && app.type === 'tiles' && app.tiles) {
              app.tiles.forEach((t: TileBigNumber) => {
                if (t.id) existingTileIds.add(t.id);
              });
            }
          });
        });
      });

      // Add existing tiles IDs
      existingTiles.forEach(t => {
        if (t.id) existingTileIds.add(t.id);
      });

      const newTileId = generateUniqueUUID(existingTileIds);
      const newAppId = generateUniqueUUID(existingAppIds);

      return {
        id: newAppId,
        type: 'tiles',
        name: 'Tiles',
        metadata: null,
        layout: null,
        tiles: [...existingTiles, { ...tile, id: newTileId }]
      };
    },
    [allDashboards]
  );

  const handleMoveToTab = useCallback(
    (dashboardName: string, tabName: string, existingTabs: DashboardTab) => {
      if (isPending) return;

      const tabValue = existingTabs[tabName];
      const existingApps = tabValue ? getTabContent(tabValue) : [];
      // Find existing tiles widget in the tab
      const existingTilesWidget = existingApps.find(
        (app: any): app is TilesWidgetV2 => 'type' in app && app.type === 'tiles'
      );

      const existingTiles = existingTilesWidget?.tiles || [];
      const tilesWidget = convertTileToTilesWidget(tile, existingTiles);

      // Build updated apps array
      const updatedApps = existingTilesWidget
        ? existingApps.map((app: any) =>
            'type' in app && app.type === 'tiles' ? tilesWidget : app
          )
        : [...existingApps, tilesWidget];

      // Build the full dashboard structure to preserve all tabs
      const payload: DashboardTab = {
        ...existingTabs,
        [tabName]: {
          ...existingTabs[tabName],
          content: updatedApps as MarketApps
        }
      };

      // If moving within same dashboard, update draft and remove from current
      if (
        dashboardName === effectiveCurrentDashboardName ||
        dashboardName === initialDashboardName
      ) {
        onMoveToDraft(tabName, tile);
        onRemoveFromCurrent();
      } else {
        // Move to different dashboard
        moveTileToDashboard({
          dashboardName,
          tabName,
          payload
        });
        onRemoveFromCurrent();
      }
    },
    [
      isPending,
      tile,
      convertTileToTilesWidget,
      effectiveCurrentDashboardName,
      initialDashboardName,
      onMoveToDraft,
      onRemoveFromCurrent,
      moveTileToDashboard
    ]
  );

  const handleCreateNewTab = useCallback(
    (dashboardName: string, existingTabs: DashboardTab) => {
      if (isPending) return;

      const tilesWidget = convertTileToTilesWidget(tile);

      // Generate unique tab name
      const existingTabNames = Object.keys(existingTabs);
      const newTabName = getUniqueTabName(existingTabNames);

      // Build the full dashboard structure with new tab
      const payload: DashboardTab = {
        ...existingTabs,
        [newTabName]: {
          id: generateUniqueUUID(new Set()),
          content: [tilesWidget] as MarketApps
        }
      };

      // If moving within same dashboard, update draft and remove from current
      if (
        dashboardName === effectiveCurrentDashboardName ||
        dashboardName === initialDashboardName
      ) {
        onMoveToDraft(newTabName, tile);
        onRemoveFromCurrent();
      } else {
        // Move to different dashboard
        moveTileToDashboard({
          dashboardName,
          tabName: newTabName,
          payload
        });
        onRemoveFromCurrent();
      }
    },
    [
      isPending,
      tile,
      convertTileToTilesWidget,
      effectiveCurrentDashboardName,
      initialDashboardName,
      onMoveToDraft,
      onRemoveFromCurrent,
      moveTileToDashboard
    ]
  );

  const handleDashboardMouseEnter = useCallback((dashboardName: string) => {
    if (subMenuTimeoutRef.current) {
      clearTimeout(subMenuTimeoutRef.current);
    }
    setHoveredDashboard(dashboardName);
  }, []);

  const handleDashboardMouseLeave = useCallback(() => {
    subMenuTimeoutRef.current = setTimeout(() => {
      setHoveredDashboard(null);
    }, 150);
  }, []);

  const handleSubMenuMouseEnter = useCallback(() => {
    if (subMenuTimeoutRef.current) {
      clearTimeout(subMenuTimeoutRef.current);
    }
  }, []);

  const handleSubMenuMouseLeave = useCallback(() => {
    subMenuTimeoutRef.current = setTimeout(() => {
      setHoveredDashboard(null);
    }, 150);
  }, []);

  // Build dashboard list: include draft dashboard name if different from initial
  const dashboardNamesList = useMemo(() => {
    const names = new Set(Object.keys(allDashboards || {}));
    // If draft name is different from initial, replace it in the list
    if (draftDashboardName && draftDashboardName !== initialDashboardName) {
      names.delete(initialDashboardName);
      names.add(draftDashboardName);
    }
    return Array.from(names).sort((a, b) => {
      if (a === effectiveCurrentDashboardName) return -1;
      if (b === effectiveCurrentDashboardName) return 1;
      return a.localeCompare(b);
    });
  }, [allDashboards, draftDashboardName, initialDashboardName, effectiveCurrentDashboardName]);

  // Get tabs for current dashboard from drafts
  const currentDashboardTabsFromDraft = useMemo(() => {
    if (hoveredDashboard !== effectiveCurrentDashboardName) return null;
    const allDrafts = getAllTabDrafts();
    const tabs: Record<string, { id: string; content: MarketApps }> = {};

    // Map tab names to tab IDs using draftTabs if available
    draftTabNames.forEach(tabName => {
      // Find the tab ID for this tab name using draftTabs mapping
      const tab = draftTabs?.find(t => t.name === tabName);
      const draftTabId = tab?.id;

      // Filter out current tab by comparing IDs
      if (draftTabId === currentTabId) return;

      if (draftTabId) {
        const draft = allDrafts[draftTabId];
        if (draft) {
          // Build MarketApps from draft
          const apps: MarketApps = [];
          // Add tiles widget if tiles exist
          if (draft.tiles.length > 0) {
            apps.push({
              id: 'tiles',
              type: 'tiles',
              name: 'Tiles',
              metadata: null,
              layout: null,
              tiles: draft.tiles
            });
          }
          // Add market tab apps
          apps.push(...draft.marketTabApps);

          // Use the name from edit mode (draftTabNames), not from server config
          tabs[tabName] = {
            id: generateUniqueUUID(new Set()),
            content: apps
          };
        }
      }
    });

    return tabs;
  }, [
    hoveredDashboard,
    effectiveCurrentDashboardName,
    getAllTabDrafts,
    draftTabNames,
    draftTabs,
    currentTabId
  ]);

  // Get tabs for other dashboards from server data
  const hoveredDashboardTabs = useMemo(() => {
    if (!hoveredDashboard) return null;
    if (hoveredDashboard === effectiveCurrentDashboardName) {
      return currentDashboardTabsFromDraft;
    }
    // For other dashboards, use server data (or draft name if it matches)
    const dashboardKey =
      hoveredDashboard === draftDashboardName ? initialDashboardName : hoveredDashboard;
    return allDashboards?.[dashboardKey] || null;
  }, [
    hoveredDashboard,
    effectiveCurrentDashboardName,
    currentDashboardTabsFromDraft,
    allDashboards,
    draftDashboardName,
    initialDashboardName
  ]);

  return (
    <Popper
      anchorEl={anchorEl}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 4]
          }
        }
      ]}
      open={open}
      placement='bottom-start'
      sx={{ zIndex: 10000 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          sx={{
            backgroundColor: 'secondary.main',
            minWidth: 200
          }}
        >
          {/* Dashboard List */}
          {dashboardNamesList.map(dashboardName => (
            <Box
              key={dashboardName}
              ref={(el: HTMLDivElement | null) => {
                if (el) {
                  dashboardItemRefs.current.set(dashboardName, el);
                  // Update ref state if this is the hovered dashboard
                  if (dashboardName === hoveredDashboard) {
                    setHoveredDashboardRef(el);
                  }
                } else {
                  dashboardItemRefs.current.delete(dashboardName);
                  // Clear ref state if this was the hovered dashboard
                  if (dashboardName === hoveredDashboard) {
                    setHoveredDashboardRef(null);
                  }
                }
              }}
              sx={hoveredDashboard === dashboardName ? activeMenuItemStyles : menuItemStyles}
              onMouseEnter={() => {
                handleDashboardMouseEnter(dashboardName);
              }}
              onMouseLeave={handleDashboardMouseLeave}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600
                }}
              >
                {dashboardName}
              </Typography>
              <ChevronRight sx={{ fontSize: 20, opacity: 0.7 }} />
            </Box>
          ))}

          {/* Sub-menu for tabs */}
          {hoveredDashboard && hoveredDashboardTabs && hoveredDashboardRef && (
            <Popper
              open
              anchorEl={hoveredDashboardRef}
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [0, -8]
                  }
                }
              ]}
              placement='right-start'
              sx={{ zIndex: 10001 }}
            >
              <Paper
                sx={{
                  backgroundColor: 'secondary.main',
                  minWidth: 180,
                  ml: 0.5
                }}
                onMouseEnter={handleSubMenuMouseEnter}
                onMouseLeave={handleSubMenuMouseLeave}
              >
                {/* Tab List */}
                {Object.keys(hoveredDashboardTabs).map(tabName => (
                  <Box
                    key={tabName}
                    sx={menuItemStyles}
                    onClick={() => {
                      handleMoveToTab(hoveredDashboard, tabName, hoveredDashboardTabs);
                    }}
                  >
                    {tabName}
                  </Box>
                ))}

                {/* New Tab Option */}
                <Box
                  sx={{
                    ...menuItemStyles
                  }}
                  onClick={() => {
                    handleCreateNewTab(hoveredDashboard, hoveredDashboardTabs);
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddOutlined sx={{ fontSize: 18 }} />
                    <Typography
                      fontSize={12}
                      sx={{ color: 'text.secondary' }}
                    >
                      New page
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Popper>
          )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default MoveTileToDashboardMenu;
